'use client';

import { CheckSquareOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client/react';
import { App, ConfigProvider } from 'antd';
import { type JSX, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { FormProvider, useForm } from 'react-hook-form';
import { Provider } from 'react-redux';
import { FacebookIcon, FacebookShareButton } from 'react-share';
import PostsTags from 'src/shared/components/posts-tags';

import findOnePostQuery from '~/shared/graphql-queries/posts/find-one-post-query';
import type { PostType } from '~/shared/types/post-type';
import Banner from '~/shared/components/banner';
import BannerLoader from '~/shared/components/banner-loader';
import ContactsRequestForm from '~/shared/components/contacts-request-form';
import Content from '~/shared/components/content';
import FacebookFeedback from '~/shared/components/facebook-feedback';
import Link from '~/shared/components/link';
import { MediaContextProvider } from '~/shared/components/media-query';
import PostLoader from '~/shared/components/post-loader';
import SmallRequestForm from '~/shared/components/small-request-form';
import StyledComponentsRegistry from '~/shared/components/styled-components-registry';
import { appSettings as settings } from '~/shared/configs/config';
import { getPageConfig } from '~/shared/configs/pages/get-page-config';
import { CONSULTATION_TYPE, PAGE_NAMES } from '~/shared/constants';
import { createOneFavoriteTopicMutation } from '~/shared/graphql-queries/favorite-topics';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import { getUrl } from '~/shared/helpers/string-helpers';
import { useTypedTranslation } from '~/shared/hooks';
import { ApolloWrapper } from '~/shared/libs/apollo/apollo-provider';
import store from '~/shared/store';

import '~/app/(app)/[lng]/posts/[alias]/components/post/post.scss';

type PostPropsType = {
  lng: string | null;
  alias: string;
  initialPost?: PostType & { isAddedToFavorite?: boolean };
};

const Post = ({ lng, alias, initialPost }: PostPropsType): JSX.Element => {
  const t = useTypedTranslation(['text', 'field', 'modal']);
  const [createOneFavoriteTopic] = useMutation(createOneFavoriteTopicMutation);

  const pageConfig = getPageConfig({
    t,
    path: PAGE_NAMES.POST,
    alias,
  });

  const {
    loading: queryLoading,
    refetch,
    data,
  } = useQuery<{
    findOnePostQuery: PostType & { isAddedToFavorite?: boolean };
  }>(findOnePostQuery, {
    variables: { filter: { alias, language: lng } },
    skip: Boolean(initialPost) || !alias || !lng,
    fetchPolicy: 'cache-first',
  });
  const post = data?.findOnePostQuery ?? initialPost;
  const loading = !post && queryLoading;
  const favoriteTopicCreate = async () => {
    try {
      await createOneFavoriteTopic({
        variables: {
          type: 'post',
          entity: post?._id,
        },
      });
      await refetch();
    } catch (error) {
      console.warn('Could not update post favorite', error);
    }
  };

  const methods = useForm({
    defaultValues: {},
    mode: 'onChange',
    shouldUnregister: false,
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && post?.content) {
      setTimeout(() => {
        const placeholder = document.getElementById('react-placeholder');
        if (!placeholder) return;

        ReactDOM.createRoot(placeholder).render(
          <StyledComponentsRegistry>
            <ApolloWrapper>
              <ConfigProvider theme={settings.theme}>
                <App>
                  <Provider store={store}>
                    <MediaContextProvider disableDynamicMediaQueries>
                      <FormProvider {...methods}>
                        <SmallRequestForm />
                      </FormProvider>
                    </MediaContextProvider>
                  </Provider>
                </App>
              </ConfigProvider>
            </ApolloWrapper>
          </StyledComponentsRegistry>,
        );
      }, [2000]);
    }
  }, [post?.content]);

  return (
    <article itemScope itemType="http://schema.org/Article" className="post__wrapper">
      {loading ? (
        <BannerLoader />
      ) : (
        <Banner
          backButtonText={t('text.return-to-blog')}
          backButtonClick={() =>
            window.location.assign(
              getLocaleLink(
                getUrl({
                  link: PAGE_NAMES.BLOG,
                }),
                lng,
              ),
            )
          }
          breadCrumbs={[
            {
              name: t('text.prana-club'),
              href: getLocaleLink(PAGE_NAMES.HOME, lng),
            },
            {
              name: t('page.blog.title'),
              href: getLocaleLink(PAGE_NAMES.BLOG, lng),
            },
            {
              name: post?.category?.name,
              href: getLocaleLink(
                getUrl({
                  link: PAGE_NAMES.CATEGORY,
                  alias: post?.category?.alias,
                }),
                lng,
              ),
            },
          ]}
          lng={lng}
          post={post}
          t={t}
        />
      )}
      <Content isPageContent>
        <div className="post">
          <div className="post__container">
            <div className="post__content">
              {loading ? (
                <PostLoader />
              ) : (
                <>
                  <div
                    itemProp="articleBody"
                    className="post__static-content"
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{
                      __html: post?.content ?? '',
                    }}
                  />
                  <section className="post__favorite-block">
                    <div className="post__favorite-text">
                      {post?.isAddedToFavorite
                        ? t('text.you-already-added-to-favorite', {
                            ns: ['text'],
                          })
                        : t('text.add-to-favorite')}
                    </div>
                    {!post?.isAddedToFavorite && (
                      <div className="post__favorite-icon">
                        <CheckSquareOutlined onClick={favoriteTopicCreate} />
                      </div>
                    )}
                  </section>
                  {post?.category?.name && (
                    <section className="post__category-block">
                      <span className="post__category-label">{t('text.category')}</span>
                      <span className="post__category-name">
                        <Link
                          itemProp="url"
                          href={getLocaleLink(
                            getUrl({
                              link: PAGE_NAMES.CATEGORY,
                              alias: post?.category?.alias,
                            }),
                            lng,
                          )}
                        >
                          {post?.category?.name}
                        </Link>
                      </span>
                    </section>
                  )}
                  <section className="post__info">
                    <div className="post__social-block">
                      <span className="post__social-block-label">{t('text.share-in-social-network')}</span>
                      <div className="post__social-icons">
                        <FacebookShareButton
                          url={`${settings.host}:${settings?.port ?? ''}/posts/${pageConfig?.alias}`}
                        >
                          <FacebookIcon round size={30} />
                        </FacebookShareButton>
                      </div>
                    </div>
                  </section>
                  <section>
                    <FacebookFeedback alias={post?.alias} />
                  </section>
                </>
              )}
            </div>
            {post?.videos?.length > 0 && (
              <section className="post__videos-container-block">
                <span className="post__short-description-label">{t('text.post-videos')}</span>
                <div className="post__videos-block">
                  {post?.videos?.map((video, i) => {
                    return (
                      <div itemScope itemType="http://schema.org/VideoObject" key={video ?? i}>
                        <link itemProp="url" href={video} />
                        <iframe
                          key={video ?? i}
                          src={video}
                          title="YouTube video player"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                        />
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            {post?.tags?.length > 0 && (
              <section className="post__same-posts">
                <PostsTags
                  withoutBreadcrumbs
                  withLoadMore={false}
                  withNoForm
                  perPage={4}
                  withNoTitle
                  tagAlias={post?.tags?.[0]?.alias}
                  lng={lng}
                />
              </section>
            )}
          </div>
        </div>
      </Content>
      <section className="post__contacts-request-form">
        <ContactsRequestForm type={CONSULTATION_TYPE.ZERO_LESSON} t={t} />
      </section>
    </article>
  );
};

export default Post;
